import {
  Button,
  ContentLayout,
  Header,
  SpaceBetween,
} from '@cloudscape-design/components';
import { userFromFirestore } from '@parallel-fluidics/shared/models/users';
import { OrderList } from 'components/Orders';
import OrderDetailProvider from 'components/Orders/orderDetailProvider';
import UserList from 'components/Users/userList';
import AddUserModal from 'features/user/components/addUserModal';
import {
  collection,
  getFirestore,
  or,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useGetOrgById } from 'hooks/use-basicQueries';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

export default function OrgDetailPage() {
  const { orgId } = useParams();
  const [addUserOpen, setAddUserOpen] = useState(false);
  const { data: orgData, isFetching, error } = useGetOrgById(orgId);

  const ordersQuery = useMemo(() => {
    if (!orgId) return null;
    return query(
      collection(getFirestore(), 'orders'),
      where('organization', '==', orgId),
      orderBy('orderDate', 'desc'),
    );
  }, [orgId]);

  const usersQuery = useMemo(() => {
    if (!orgId || isFetching) return null;
    if (orgData?.domains && orgData.domains.length > 0) {
      return query(
        collection(getFirestore(), 'users'),
        or(
          where('organization', '==', orgId),
          where('domain', 'in', orgData.domains),
        ),
      );
    }
    return query(
      collection(getFirestore(), 'users'),
      where('organization', '==', orgId),
    );
  }, [orgData?.domains, orgId, isFetching]);

  const { data: orders, status: ordersStatus } = useRealtimeDocs({ query: ordersQuery });
  const { data: users, status: usersStatus } = useRealtimeDocs({
    query: usersQuery,
    transformData: userFromFirestore,
  });

  const { usersFromOrg, usersFromDomain } = useMemo(() => {
    const returnData = {
      usersFromOrg: [],
      usersFromDomain: [],
    };
    if (users?.length > 0) {
      users
        // sort here so we don't need to build index in firestore
        .sort((a, b) => a.email.localeCompare(b.email))
        .forEach((user) => {
          if (user.organization === orgId) {
            returnData.usersFromOrg.push(user);
          } else {
            returnData.usersFromDomain.push(user);
          }
        });
    }
    return returnData;
  }, [orgId, users]);

  if (isFetching) {
    return <span>{`${orgId} (loading organization...)`}</span>;
  }

  if (error) {
    return <p>{error.message}</p>;
  }

  return (
    <ContentLayout
      header={(
        <Header
          variant="h1"
        >
          <span>{orgData.name}</span>
        </Header>
    )}
    >
      <SpaceBetween size="m">
        {ordersQuery && (
          <OrderDetailProvider>
            <OrderList
              stickyHeader
              header={(
                <Header>
                  Orders
                </Header>
              )}
              orders={orders}
              isLoading={ordersStatus === 'loading'}
            />
          </OrderDetailProvider>
        )}

        {usersQuery && (
          <UserList
            users={usersFromOrg}
            isLoading={usersStatus === 'loading'}
            header={(
              <Header
                description="Users who belong to this organization."
                actions={<Button onClick={() => setAddUserOpen(true)}>Add user</Button>}
              >
                Users
              </Header>
            )}
          />
        )}

        {usersFromDomain.length > 0 && (
          <UserList
            users={usersFromDomain}
            isLoading={usersStatus === 'loading'}
            header={(
              <Header
                description="Users who share the same email domain as this organization but are not members."
              >
                Recommended Users
              </Header>
            )}
          />
        )}
      </SpaceBetween>
      {addUserOpen && (
        <AddUserModal
          open
          organization={orgData}
          onClose={() => setAddUserOpen(false)}
        />
      )}
    </ContentLayout>
  );
}
